import clsx from 'clsx'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

export type ToastSeverity = 'info' | 'success' | 'warn' | 'error'

export interface ToastInput {
  title: string
  description?: string
  severity?: ToastSeverity
  /** Milliseconds. 0 = sticky. Defaults to 4500. */
  duration?: number
}

interface ToastInternal extends Required<Pick<ToastInput, 'title'>> {
  id: string
  description?: string
  severity: ToastSeverity
  duration: number
}

interface ToastApi {
  show(opts: ToastInput): string
  dismiss(id: string): void
}

const ToastContext = createContext<ToastApi | null>(null)

const severityClass: Record<ToastSeverity, string> = {
  info: 'border-[var(--border)] bg-[var(--bg-3)]',
  success: 'border-[var(--green)]/40 bg-[var(--bg-3)]',
  warn: 'border-[var(--amber)]/50 bg-[var(--bg-3)]',
  error: 'border-[var(--red)]/50 bg-[var(--bg-3)]',
}

const severityDot: Record<ToastSeverity, string> = {
  info: 'bg-[var(--accent)]',
  success: 'bg-[var(--green)]',
  warn: 'bg-[var(--amber)]',
  error: 'bg-[var(--red)]',
}

function newId(): string {
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
}

export interface ToasterProps {
  children?: ReactNode
}

/** Provider + viewport. Wrap the app once. */
export function Toaster({ children }: ToasterProps) {
  const [toasts, setToasts] = useState<ToastInternal[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    const t = timers.current.get(id)
    if (t) {
      clearTimeout(t)
      timers.current.delete(id)
    }
    setToasts((prev) => prev.filter((x) => x.id !== id))
  }, [])

  const show = useCallback(
    ({ title, description, severity = 'info', duration = 4500 }: ToastInput) => {
      const id = newId()
      const toast: ToastInternal = { id, title, description, severity, duration }
      setToasts((prev) => [...prev, toast])
      if (duration > 0) {
        const handle = setTimeout(() => dismiss(id), duration)
        timers.current.set(id, handle)
      }
      return id
    },
    [dismiss],
  )

  useEffect(() => {
    return () => {
      for (const t of timers.current.values()) clearTimeout(t)
      timers.current.clear()
    }
  }, [])

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div
              role="region"
              aria-live="polite"
              aria-label="Notifications"
              className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
            >
              {toasts.map((t) => (
                <div
                  key={t.id}
                  role="status"
                  className={clsx(
                    'min-w-[260px] max-w-sm rounded-[var(--radius)] border px-3 py-2 text-[13px] text-[var(--text)] shadow-lg',
                    severityClass[t.severity],
                  )}
                >
                  <div className="flex items-start gap-2">
                    <span
                      aria-hidden="true"
                      className={clsx(
                        'mt-1 h-2 w-2 rounded-full shrink-0',
                        severityDot[t.severity],
                      )}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{t.title}</div>
                      {t.description ? (
                        <div className="text-[var(--text-2)]">{t.description}</div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss notification"
                      onClick={() => dismiss(t.id)}
                      className="text-[var(--text-2)] hover:text-[var(--text)] outline-none focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-sm)] px-1"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used inside <Toaster>')
  }
  return ctx
}
